import { Button } from 'primereact/button';
import React from 'react';
import * as XLSX from 'xlsx';
import { trackToolAction } from '../../utils/analytics';
import { resolveToolName } from '../../utils/analyticsToolMap';

// Excel has a maximum cell character limit of 32,767
const EXCEL_MAX_CELL_LENGTH = 32767;

export interface ExcelExportProps<T> {
    data: T[];
    fileName: string;
    columnsToExport: (item: T) => Record<string, any>; // Function to transform data for export
    /** Optional async work (e.g. fetch claim JSON) before transforming rows. */
    prepareExport?: (data: T[]) => Promise<void>;
}

// Truncate string values that exceed Excel's cell character limit
const truncateForExcel = (value: any): any => {
    if (typeof value === 'string' && value.length > EXCEL_MAX_CELL_LENGTH) {
        return value.substring(0, EXCEL_MAX_CELL_LENGTH - 20) + '... [TRUNCATED]';
    }
    return value;
};

// Process a row to truncate all string values
const processRowForExcel = (row: Record<string, any>): Record<string, any> => {
    const processedRow: Record<string, any> = {};
    for (const [key, value] of Object.entries(row)) {
        processedRow[key] = truncateForExcel(value);
    }
    return processedRow;
};

const ExcelExport: React.FC<ExcelExportProps<any>> = ({ data, fileName, columnsToExport, prepareExport }) => {
    const [exporting, setExporting] = React.useState(false);

    const exportToExcel = async () => {
        if (!data || data.length === 0) {
            console.error("No data available to export.");
            return;
        }

        trackToolAction(resolveToolName(window.location.pathname), "export");
        setExporting(true);
        try {
            if (prepareExport) {
                await prepareExport(data);
            }
            const transformedData = data.map(columnsToExport).map(processRowForExcel);
            const ws = XLSX.utils.json_to_sheet(transformedData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
            XLSX.writeFile(wb, `${fileName}.xlsx`);
        } catch (error) {
            console.error("Error exporting to Excel:", error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Button
        label={exporting ? "Exporting..." : "Export to Excel"}
        onClick={exportToExcel}
        severity="success"
        icon="pi pi-file-excel"
        loading={exporting}
        disabled={exporting}
      />    );
};

export default ExcelExport;
